/**
 * configDb.js
 *
 * IndexedDB persistence for the salon's public config blobs (content-addressed
 * by hash). One record per (salon_id, config_type) holding the LATEST blob we
 * have for that type — replaced in place whenever the hash changes, so the DB
 * never accumulates stale versions.
 *
 * Consumed by apis/vitrinaConfig.js (two-tier loader): the manifest's hashes
 * are diffed against what's stored here, and only missing/changed blobs are
 * fetched over the network.
 *
 * Every operation degrades gracefully: if IndexedDB is unavailable (private
 * browsing edge cases, storage pressure) reads resolve to empty and writes
 * no-op, so the vitrina still works — it just re-downloads on each visit.
 */

const DB_NAME = 'vitrina-config';
const DB_VERSION = 1;
const STORE = 'configs';

/** Composite primary key — one record per salon+type. */
const record_key = (salon_id, config_type) => `${salon_id}:${config_type}`;

let db_promise = null;

/**
 * Open (and memoize) the database. Resolves to null when IndexedDB is
 * unavailable or the open fails — callers treat null as "no cache".
 * @returns {Promise<IDBDatabase|null>}
 */
function open_db() {
  if (db_promise) return db_promise;
  db_promise = new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') return resolve(null);
    let request;
    try {
      request = indexedDB.open(DB_NAME, DB_VERSION);
    } catch (e) {
      console.warn('[vitrina configDb] IndexedDB unavailable:', e);
      return resolve(null);
    }
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'key' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => {
      console.warn('[vitrina configDb] open failed:', request.error);
      resolve(null);
    };
    request.onblocked = () => resolve(null);
  });
  return db_promise;
}

/**
 * Read every stored config blob for a salon.
 * @param {string} salon_id
 * @returns {Promise<Object<string, {hash: string, data: any}>>} map keyed by
 *   config_type; empty object when nothing is stored or IDB is unavailable.
 */
export async function get_stored_configs(salon_id) {
  const db = await open_db();
  if (!db) return {};
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).getAll();
      req.onsuccess = () => {
        const out = {};
        for (const rec of req.result || []) {
          if (rec.salon_id === String(salon_id)) {
            out[rec.config_type] = { hash: rec.hash, data: rec.data };
          }
        }
        resolve(out);
      };
      req.onerror = () => resolve({});
    } catch (e) {
      console.warn('[vitrina configDb] read failed:', e);
      resolve({});
    }
  });
}

/**
 * Store (replace) the blob for one config type.
 * @param {string} salon_id
 * @param {string} config_type
 * @param {string} hash
 * @param {any} data
 * @returns {Promise<void>} resolves even on failure (write is best-effort).
 */
export async function put_config(salon_id, config_type, hash, data) {
  const db = await open_db();
  if (!db) return;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put({
        key: record_key(salon_id, config_type),
        salon_id: String(salon_id),
        config_type,
        hash,
        data,
        saved_at: Date.now(),
      });
      tx.oncomplete = () => resolve();
      tx.onerror = () => {
        console.warn('[vitrina configDb] write failed:', tx.error);
        resolve();
      };
    } catch (e) {
      console.warn('[vitrina configDb] write failed:', e);
      resolve();
    }
  });
}
